import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import useQuery from '@/hooks/useQuery'
import { Modal } from '@/shared/Modal'
import { useGlobalState } from '@/state'

// We only consider showing this modal on the dashboard, that's where users
// land when they log in, so a "what's new" prompt fits the context instead
// of interrupting whatever else they came to do.
const ALLOWED_PATH = '/dashboard'

const STORAGE_KEY = 'clc:changelog-seen'
const SHOW_DELAY_MS = 1500

const readSeen = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch (_e) {
    return []
  }
}

const markSeen = (id) => {
  try {
    const seen = readSeen()
    if (seen.includes(id)) return
    // Cap at 100 to keep storage tidy.
    const next = [id, ...seen].slice(0, 100)
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch (_e) {
    // localStorage can be unavailable (Safari private mode etc.). Swallow.
  }
}

const ChangelogAnnouncementModal = () => {
  const [currentUser] = useGlobalState('currentUser')
  const { getRequest } = useQuery()
  const location = useLocation()
  const [entry, setEntry] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  // Once we've fetched & decided this session, don't re-hit the API on
  // every navigation back to /dashboard.
  const checkedRef = useRef(false)

  useEffect(() => {
    if (!currentUser?.id) return
    if (location.pathname !== ALLOWED_PATH) return
    if (checkedRef.current) return
    checkedRef.current = true

    let cancelled = false
    let timer = null

    getRequest('/api/v1/changelog_entries/latest_for_modal', {}, (err, data) => {
      if (cancelled) return
      if (err) return
      const e = data?.entry
      if (!e) return
      if (readSeen().includes(e.id)) return

      timer = window.setTimeout(() => {
        if (cancelled) return
        setEntry(e)
        setIsOpen(true)
      }, SHOW_DELAY_MS)
    })

    return () => {
      cancelled = true
      if (timer) window.clearTimeout(timer)
    }
  }, [currentUser?.id, location.pathname])

  const close = () => {
    if (entry) markSeen(entry.id)
    setIsOpen(false)
  }

  if (!entry) return null

  return (
    <Modal isOpen={isOpen} closeModal={close}>
      <div className='mt-2'>
        {entry.coverImageUrl && (
          <img src={entry.coverImageUrl} alt='' className='w-full h-auto max-h-64 object-cover rounded-lg mb-4' />
        )}
        <p className='text-xs font-semibold uppercase tracking-wider text-clcpurple mb-2'>
          What&apos;s new · {entry.publishedAtLabel}
        </p>
        <h2 className='text-2xl font-bold text-slate-900 dark:text-white mb-3'>{entry.title}</h2>
        <div
          className='prose prose-sm dark:prose-invert max-w-none text-slate-800 dark:text-slate-100 changelog-body'
          dangerouslySetInnerHTML={{ __html: entry.bodyHtml }}
        />
        <div className='flex items-center justify-end gap-3 mt-6'>
          <button
            type='button'
            onClick={close}
            className='px-3 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
          >
            Got it
          </button>
          <Link
            to='/changelog'
            onClick={close}
            className='px-4 py-2 text-sm font-semibold rounded-lg text-white bg-clcpurple hover:bg-clcpurple-dark'
          >
            Read the full changelog
          </Link>
        </div>
      </div>
    </Modal>
  )
}

export default ChangelogAnnouncementModal
